import React from "react";
import {
  Storefront,
  CaretUp,
  CaretDown,
  Minus,
  CheckSquare,
  Square,
  ChartLine,
  PushPin,
  PushPinSlash,
  Bell,
  BellRinging,
  CircleNotch,
  Scales
} from "@phosphor-icons/react";

export function MartTable({
  loading,
  prices,
  selectedMarts,
  watchlistNames,
  martAlerts,
  selectedCategory,
  onToggleSelection,
  onToggleWatchlist,
  onShowHistory,
  onShowAlert,
  formatPrice,
  getChangeColor,
  getChangeBg,
  currentCategory,
  user
}) {
  return (
    <div className="bg-white border border-[#E5E7EB] overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="marts-table">
          <thead className="bg-[#F3F4F6]">
            <tr>
              <th className="text-center px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#9CA3AF] w-12">
                <Scales size={14} className="mx-auto" />
              </th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#4B5563]">Mart</th>
              <th className="text-left px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#4B5563]">County</th>
              <th className="text-center px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#4B5563]">Day</th>
              <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#4B5563]">Price</th>
              <th className="text-right px-4 py-3 text-xs font-mono uppercase tracking-wider text-[#4B5563]">Change</th>
              <th className="text-center px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#9CA3AF] w-12">
                <ChartLine size={14} className="mx-auto" />
              </th>
              <th className="text-center px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#9CA3AF] w-12">
                <PushPin size={14} className="mx-auto" />
              </th>
              <th className="text-center px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#9CA3AF] w-12">
                <Bell size={14} className="mx-auto" />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E7EB]">
            {loading ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center">
                  <CircleNotch size={32} className="animate-spin text-[#0F766E] mx-auto mb-2" />
                  <span className="text-[#6B7280] text-sm">Loading mart prices...</span>
                </td>
              </tr>
            ) : prices.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-[#6B7280]">
                  No marts found matching your criteria.
                </td>
              </tr>
            ) : (
              prices.map((price, index) => {
                const isSelected = selectedMarts.includes(price.mart_name);
                const hasAlert = martAlerts.some(a => a.mart_name === price.mart_name && a.category === selectedCategory);
                const isWatched = watchlistNames.includes(price.mart_name);
                return (
                  <MartRow
                    key={price.id || `mart-${index}`}
                    price={price}
                    index={index}
                    isSelected={isSelected}
                    isWatched={isWatched}
                    hasAlert={hasAlert}
                    onToggleSelection={onToggleSelection}
                    onToggleWatchlist={onToggleWatchlist}
                    onShowHistory={onShowHistory}
                    onShowAlert={onShowAlert}
                    formatPrice={formatPrice}
                    getChangeColor={getChangeColor}
                    getChangeBg={getChangeBg}
                    currentCategory={currentCategory}
                    user={user}
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MartRow({
  price,
  index,
  isSelected,
  isWatched,
  hasAlert,
  onToggleSelection,
  onToggleWatchlist,
  onShowHistory,
  onShowAlert,
  formatPrice,
  getChangeColor,
  getChangeBg,
  currentCategory,
  user
}) {
  return (
    <tr 
      className={`hover:bg-[#F9FAFB] transition-colors ${isSelected ? 'bg-[#0F766E]/10' : ''}`}
      data-testid={`mart-row-${index}`}
    >
      <td className="px-3 py-3 text-center">
        <button 
          onClick={() => onToggleSelection(price.mart_name)}
          className="text-[#0F766E] hover:text-[#14B8A6]"
        >
          {isSelected ? <CheckSquare size={20} weight="fill" /> : <Square size={20} />}
        </button>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Storefront size={16} className="text-[#0F766E]" />
          <span className="text-[#111827] text-sm">{price.mart_name}</span>
          {price.data_source === "reported" && (
            <span
              className="px-1.5 py-0.5 bg-[#D1FAE5] text-[#065F46] text-[9px] font-mono uppercase tracking-wide"
              title={`Reported price from ${price.sale_date || "recent sale"}`}
              data-testid="reported-chip"
            >
              Reported
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <span className="text-[#4B5563] text-sm">{price.county}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <span className="text-[#4B5563] text-xs">{price.market_day}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="font-mono text-sm text-[#111827]">
          {formatPrice(price.price, currentCategory?.unit)}
        </span>
      </td>
      <td className="px-4 py-3 text-right">
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 ${getChangeBg(price.change)}`}>
          {price.change > 0 ? (
            <CaretUp size={12} className="text-[#059669]" weight="fill" />
          ) : price.change < 0 ? (
            <CaretDown size={12} className="text-[#DC2626]" weight="fill" />
          ) : (
            <Minus size={12} className="text-[#4B5563]" />
          )}
          <span className={`font-mono text-xs ${getChangeColor(price.change)}`}>
            {price.change >= 0 ? "+" : ""}{price.change?.toFixed(2) || "0.00"}%
          </span>
        </div>
      </td>
      <td className="px-3 py-3 text-center">
        <button
          onClick={() => onShowHistory(price)}
          className="p-1 text-[#6B7280] hover:text-[#0F766E] transition-colors"
          title="View Price History"
        >
          <ChartLine size={18} />
        </button>
      </td>
      <td className="px-3 py-3 text-center">
        {user && (
          <button
            onClick={() => onToggleWatchlist(price.mart_name)}
            className={`p-1 transition-colors ${isWatched ? 'text-[#FCD34D]' : 'text-[#6B7280] hover:text-[#FCD34D]'}`}
            title={isWatched ? "Remove from Watchlist" : "Add to Watchlist"}
          >
            {isWatched ? <PushPinSlash size={18} weight="fill" /> : <PushPin size={18} />}
          </button>
        )}
      </td>
      <td className="px-3 py-3 text-center">
        {user && (
          <button
            onClick={() => onShowAlert(price)}
            className={`p-1 transition-colors ${hasAlert ? 'text-[#F59E0B]' : 'text-[#6B7280] hover:text-[#F59E0B]'}`}
            title={hasAlert ? "Manage Alert" : "Set Price Alert"}
          >
            {hasAlert ? <BellRinging size={18} weight="fill" /> : <Bell size={18} />}
          </button>
        )}
      </td>
    </tr>
  );
}

export default MartTable;
